import {
  MAX_PDP_HINWEIS_ZEICHEN,
  type PdpAktion,
  type PdpEntscheidung,
  type PdpEntscheidungsAngaben,
} from '@awf/shared';
import { useId, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ApiError } from '../api/client.js';
import { ALLGEMEINER_FEHLERTEXT } from '../api/errors.js';
import { alsDatumZeit } from '../format.js';
import { klasse } from '../ui/klasse.js';
import stile from './entscheidung-spalte.module.css';

export const HINWEIS_VERWORFEN_TEXT =
  'Ihr Zusatzhinweis wurde vom Sicherheitsfilter nicht verwendet. Die Optimierung läuft ohne ihn.';

function Entschieden({ entscheidung }: { entscheidung: PdpEntscheidung | undefined }) {
  const e = entscheidung;
  if (!e) return <p className={stile.leise}>Über diesen Auftrag wurde bereits entschieden.</p>;
  return (
    <div className={stile.entschieden}>
      <p>
        <strong>{e.aktion === 'optimieren' ? 'Optimierung gestartet' : 'Verworfen'}</strong> am{' '}
        {alsDatumZeit(e.am)}
      </p>
      {e.aktion === 'optimieren' && (
        <>
          <p className={stile.leise}>Zusätzliche Wünsche</p>
          <p className={stile.hinweisText}>{e.zusatzhinweis || '—'}</p>
          <p>Ablage im SharePoint: {e.sharepoint ? 'Ja' : 'Nein'}</p>
        </>
      )}
      {e.hinweisVerworfen && e.zusatzhinweis && (
        <div className="eds-alert eds-alert--warning" role="status">
          <p>{HINWEIS_VERWORFEN_TEXT}</p>
        </div>
      )}
    </div>
  );
}

/**
 * Die mitlaufende Spalte der Entscheidungsseite (Spec 2026-10-07, Abschnitt 4.3). Die
 * Fehlermeldung lebt HIER und ausserhalb des Formulars: Das Backend setzt `entwurf` vor dem
 * Aufruf an n8n, der Snapshot schaltet die Spalte in den Lesemodus, waehrend die Anfrage noch
 * laeuft -- die Meldung muss den Wechsel ueberleben (Final Review 2026-10-05).
 *
 * Typneutral seit Spec 2026-10-13-ratgeber-analyse-frontend: das PDP-Tor reicht Status und
 * `pdpEntscheidung` des Auftrags herein, die Ratgeber-Analyse Stand und Entscheidung EINER Seite.
 */
export function EntscheidungSpalte({
  offen,
  entscheidung,
  seitenart,
  entscheide,
  geschlossenGrund,
  weiter,
  nurVerwerfen,
}: {
  offen: boolean;
  entscheidung: PdpEntscheidung | undefined;
  seitenart: 'Produktdetailseite' | 'Ratgeberseite';
  /**
   * Warum nicht mehr entschieden werden kann, wenn es KEINE Entscheidung gibt (Codex-Review
   * PR #73): Eine ueberholte Seite oder eine beendete Analyse ist nicht "bereits entschieden".
   */
  geschlossenGrund?: string | undefined;
  /**
   * Der naechste Schritt nach der Entscheidung (Rueckmeldung 2026-10-14): Wartet der Entwurf auf
   * die Freigabe, fuehrt "Jetzt prüfen" direkt dorthin, statt erst zurueck zur Fortschrittsseite.
   */
  weiter?: { text: string; pfad: string } | undefined;
  /**
   * Gesetzt, wenn diese Seite nur verworfen werden kann (Analyse ohne Fokus-Keyword): Der Satz
   * steht statt der Optimierungsangaben, "Jetzt optimieren" entfaellt. Das Backend weist es ohnehin ab.
   */
  nurVerwerfen?: string | undefined;
  entscheide: (aktion: PdpAktion, angaben?: PdpEntscheidungsAngaben) => Promise<void>;
}) {
  const [hinweis, setHinweis] = useState('');
  const [sharepoint, setSharepoint] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [fragtNach, setFragtNach] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // Zwei Klicks im selben Tick sehen beide noch `sendet === false` (Absende-Rennen 2026-09-29).
  const laeuft = useRef(false);
  const hinweisId = useId();
  const radioName = `${seitenart}-sharepoint`;

  function sende(aktion: PdpAktion) {
    if (laeuft.current) return;
    laeuft.current = true;
    setSendet(true);
    setFragtNach(false);
    setFehler(null);
    const angaben =
      aktion === 'optimieren' ? { zusatzhinweis: hinweis.trim(), sharepoint } : undefined;
    // Bei Erfolg bleibt die Sperre stehen: der Snapshot schaltet gleich in den Lesemodus.
    entscheide(aktion, angaben).catch((f: unknown) => {
      laeuft.current = false;
      setSendet(false);
      setFehler(f instanceof ApiError ? f.text : ALLGEMEINER_FEHLERTEXT);
    });
  }

  return (
    <aside className={stile.spalte} aria-label="Ihre Entscheidung">
      <h2 className={stile.titel}>Ihre Entscheidung</h2>
      {fehler && (
        <div className="eds-alert eds-alert--error" role="alert">
          <p>{fehler}</p>
        </div>
      )}
      {offen ? (
        <>
          {nurVerwerfen ? (
            <p className={stile.leise}>{nurVerwerfen}</p>
          ) : (
            <>
              <div className="eds-field">
                <label className="eds-field__label" htmlFor={hinweisId}>
                  Zusätzliche inhaltliche Optimierungswünsche (optional)
                </label>
                <textarea
                  id={hinweisId}
                  className={klasse('eds-field__input', stile.eingabe)}
                  value={hinweis}
                  maxLength={MAX_PDP_HINWEIS_ZEICHEN}
                  disabled={sendet}
                  placeholder="z. B. besondere Aspekte berücksichtigen, bestimmte Tonalität, Beispiele, die rein sollen"
                  onChange={(e) => setHinweis(e.target.value)}
                />
                <p className="eds-field__hint" aria-live="polite">
                  {hinweis.length.toLocaleString('de-DE')} /{' '}
                  {MAX_PDP_HINWEIS_ZEICHEN.toLocaleString('de-DE')} Zeichen
                </p>
              </div>
              <fieldset className={stile.gruppe} disabled={sendet}>
                <legend className="eds-field__label">Ablage im SharePoint</legend>
                <label>
                  <input
                    type="radio"
                    name={radioName}
                    checked={sharepoint}
                    onChange={() => setSharepoint(true)}
                  />{' '}
                  Ja
                </label>
                <label>
                  <input
                    type="radio"
                    name={radioName}
                    checked={!sharepoint}
                    onChange={() => setSharepoint(false)}
                  />{' '}
                  Nein
                </label>
              </fieldset>
            </>
          )}
          {fragtNach ? (
            <div className={stile.knoepfe}>
              <p className={stile.leise}>
                Die Seite wird dann nicht optimiert. Das lässt sich nicht rückgängig machen.
              </p>
              <button
                type="button"
                className="eds-button eds-button--primary"
                disabled={sendet}
                onClick={() => sende('verwerfen')}
              >
                Ja, verwerfen
              </button>
              <button
                type="button"
                className="eds-button eds-button--secondary"
                onClick={() => setFragtNach(false)}
              >
                Abbrechen
              </button>
            </div>
          ) : (
            <div className={stile.knoepfe}>
              {!nurVerwerfen && (
                <>
                  {/* Satz wortgleich zum n8n-Formular: die Seite ist erkannt, der Klick startet
                      die Optimierung genau dieses Seitentyps. */}
                  <p className={stile.leise}>
                    Diese Seite ist als <strong>{seitenart}</strong> erkannt. Der Klick startet die
                    dafür vorgesehene Optimierung.
                  </p>
                  <button
                    type="button"
                    className="eds-button eds-button--primary"
                    disabled={sendet}
                    onClick={() => sende('optimieren')}
                  >
                    Jetzt optimieren
                  </button>
                </>
              )}
              <button
                type="button"
                className="eds-button eds-button--secondary"
                disabled={sendet}
                onClick={() => setFragtNach(true)}
              >
                Verwerfen
              </button>
            </div>
          )}
        </>
      ) : !entscheidung && geschlossenGrund ? (
        <p className={stile.leise}>{geschlossenGrund}</p>
      ) : (
        <Entschieden entscheidung={entscheidung} />
      )}
      {!offen && weiter && (
        <Link className="eds-button eds-button--primary" to={weiter.pfad}>
          {weiter.text}
        </Link>
      )}
    </aside>
  );
}
